import React from "react";
import type { ImpressionJob } from "../../types/impressionJob";
import { Eye, Trash2, Clock, CheckCircle2, XCircle } from "lucide-react";
import { format, parseISO } from "date-fns";

interface ImpressionJobsTableProps {
  jobs: ImpressionJob[];
  loading: boolean;
  onViewDetails: (job: ImpressionJob) => void;
  onDelete: (jobId: number) => void;
}

export const ImpressionJobsTable: React.FC<ImpressionJobsTableProps> = ({
  jobs,
  loading,
  onViewDetails,
  onDelete,
}) => {
  const getStatusBadge = (status: string) => {
    const badges = {
      processing: {
        icon: Clock,
        className: "bg-yellow-100 text-yellow-800",
        text: "Processing",
      },
      completed: {
        icon: CheckCircle2,
        className: "bg-green-100 text-green-800",
        text: "Completed",
      },
      failed: {
        icon: XCircle,
        className: "bg-red-100 text-red-800",
        text: "Failed",
      },
    };

    const badge = badges[status as keyof typeof badges] || badges.processing;
    const Icon = badge.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.className}`}>
        <Icon className="w-3 h-3" />
        {badge.text}
      </span>
    );
  };

  const formatDateRange = (startDate?: string, endDate?: string) => {
    if (!startDate || !endDate) return "-";
    try {
      const start = parseISO(startDate);
      const end = parseISO(endDate);
      const startFormatted = format(start, "MMM d");
      const endFormatted = format(end, "MMM d, yyyy");

      // If same month and year, show: "Mar 1 - 15, 2025"
      if (start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()) {
        return `${startFormatted} - ${format(end, "d, yyyy")}`;
      }
      // Otherwise show: "Mar 1 - Apr 15, 2025"
      return `${startFormatted} - ${endFormatted}`;
    } catch {
      return "-";
    }
  };

  if (jobs.length === 0 && !loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
        <p className="text-gray-500">
          No impression jobs found
        </p>
        <p className="text-sm text-gray-400 mt-1">
          Create one from a campaign to get started
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Campaign ID
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Date Range
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                City
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Rows
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {jobs.map((job) => (
              <tr key={job.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {job.campaign_id}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {formatDateRange(job.analyzed_start_date, job.analyzed_end_date)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  <span className="capitalize">{job.city}</span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {getStatusBadge(job.status)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {job.row_count ? job.row_count.toLocaleString() : "-"}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex items-center justify-end space-x-2">
                    <button
                      onClick={() => onViewDetails(job)}
                      className="text-primary-600 hover:text-primary-900 p-1 rounded hover:bg-primary-50"
                      title="View Details"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => onDelete(job.id)}
                      className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
